import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

interface Question {
  id: string;
  questionText: string;
  type: string;
  options?: string[];
  correctAnswer?: string;
  correctAnswers?: string[];
  explanation?: string;
  subject: string;
  topic: string;
  subtopic?: string;
  difficultyLevel: string;
  examTypes?: string[];
  tenantId?: string | null;
  tenantCode?: string;
  tenantCodes?: string[];
  accessScope?: 'GLOBAL' | 'TENANT';
  sourceType?: 'SUPER_ADMIN' | 'ADMIN';
}

interface ViewQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  question: Question | null;
}

export const ViewQuestionDialog: React.FC<ViewQuestionDialogProps> = ({
  isOpen,
  onClose,
  question
}) => {
  if (!question) {
    return null;
  }

  const scope = question.accessScope || (question.tenantId ? 'TENANT' : 'GLOBAL');
  const source = question.sourceType === 'SUPER_ADMIN' || scope === 'GLOBAL' ? 'Super Admin' : 'Admin Tenant';
  const audience = scope === 'GLOBAL'
    ? 'All admin tenants'
    : question.tenantCodes?.join(', ') || question.tenantCode || 'Current tenant only';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Question</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <p className="text-sm font-medium">Question</p>
            <div className="rounded-md border bg-muted/30 p-4 whitespace-pre-wrap break-words">
              {question.questionText}
            </div>
          </div>

          {question.options && question.options.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Options</p>
              <div className="space-y-2">
                {question.options.map((option, index) => {
                  const isCorrect = Boolean(
                    question.correctAnswers?.includes(option) ||
                    question.correctAnswer === option ||
                    question.correctAnswer === String(index + 1)
                  );

                  return (
                    <div key={`${question.id}-${index}`} className="rounded-md border p-3 flex items-start justify-between gap-3">
                      <span className="break-words">{option}</span>
                      {isCorrect && <Badge>Correct</Badge>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {question.correctAnswer && (!question.options || question.options.length === 0) && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Correct Answer</p>
              <div className="rounded-md border p-3">{question.correctAnswer}</div>
            </div>
          )}

          {question.explanation && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Explanation</p>
              <div className="rounded-md border bg-muted/20 p-4 whitespace-pre-wrap break-words">
                {question.explanation}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Subject</p>
              <Badge variant="outline">{question.subject}</Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Topic</p>
              <Badge variant="outline">{question.topic}</Badge>
            </div>
            {question.subtopic && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Subtopic</p>
                <Badge variant="outline">{question.subtopic}</Badge>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium">Difficulty</p>
              <Badge variant="secondary">{question.difficultyLevel}</Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Question Type</p>
              <Badge variant="secondary">{question.type}</Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Access Scope</p>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={scope === 'GLOBAL' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'}>
                  {scope === 'GLOBAL' ? 'Global' : 'Tenant'}
                </Badge>
                <Badge variant="outline">{source}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">Accessible to: {audience}</p>
            </div>
          </div>

          {question.examTypes && question.examTypes.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Exam Types</p>
              <div className="flex flex-wrap gap-2">
                {question.examTypes.map((examType) => (
                  <Badge key={examType} variant="outline">{examType}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};